// The deal-entry form model and its mapping to the api `DealInputs`. The form holds raw STRINGS
// (what the user typed); `deriveDeal` parses them into the numeric/ratio shape the api expects.
//
// Convention: dollar + count fields are entered as plain numbers; RATE fields (vacancy, expense,
// exit cap) are entered as PERCENTS (e.g. "7", "5.5") and converted to ratios here, so the wire
// payload matches the engine's canonical ratios (exitCapRate 0.055, vacancyRate 0.07).
import type { DealInputs } from './api.js';

export interface DealFormState {
  noi: string;
  annualDebtService: string;
  totalCost: string;
  loanAmount: string;
  totalUnits: string;
  exitCapRatePct: string;
  vacancyRatePct: string;
  expenseRatioPct: string;
  salePrice: string;
  holdYears: string;
  assetClass: string;
  propertyClass: string;
  concessionRatePct: string;
  isLIHTC: boolean;
  reviewerContext: string;
  /**
   * Sponsor / borrower entity name. Not a deal input — it never reaches the engine and moves no
   * metric. It is the join key to the public filing record (SEC EDGAR searches by name only), and it
   * is typed by a person here rather than extracted, because a mis-extracted sponsor produces a
   * confident match against the wrong real company.
   */
  sponsorName: string;
  /**
   * Sponsor / development-team EVIDENCE — what they have built, who the principals are, which GC they
   * have run with. Distinct from `sponsorName` (WHO, an EDGAR join key) and from `reviewerContext` (a
   * class/amenity override): this is the corpus the Sponsor module judges 20 of the 100 score points
   * on. Left blank, that module is told the submission carries no sponsor information — which is what
   * it was told on every run before this field existed.
   */
  sponsorContext: string;
}

export const EMPTY_DEAL_FORM: DealFormState = {
  noi: '',
  annualDebtService: '',
  totalCost: '',
  loanAmount: '',
  totalUnits: '',
  exitCapRatePct: '',
  vacancyRatePct: '',
  expenseRatioPct: '',
  salePrice: '',
  holdYears: '',
  assetClass: '',
  propertyClass: '',
  concessionRatePct: '',
  isLIHTC: false,
  reviewerContext: '',
  sponsorName: '',
  sponsorContext: '',
};

/** "The Commons" — the standing regression anchor, in form (user-facing) units. */
export const SAMPLE_DEAL_FORM: DealFormState = {
  noi: '2220000',
  annualDebtService: '1694656',
  totalCost: '37180000',
  loanAmount: '26026000',
  totalUnits: '166',
  exitCapRatePct: '5.5',
  vacancyRatePct: '7',
  expenseRatioPct: '35',
  salePrice: '41800000',
  holdYears: '10',
  assetClass: 'B',
  propertyClass: '',
  concessionRatePct: '26.7',
  isLIHTC: false,
  reviewerContext: '166-unit wood-frame multifamily, 280 E Carver St, Durham NC.',
  // Deliberately BLANK. The Commons is a real deal, and filling this in would attribute it to a
  // sponsor entity — then search the SEC's real filing index for that name. Inventing one to make a
  // demo look complete is exactly the false-attribution the whole capture-at-upload design avoids.
  sponsorName: '',
  // Blank for a STRONGER version of the same reason. A fabricated track record here would not merely
  // sit on the record — the Sponsor module would judge it, and `sponsorValidation` would ground its
  // evidence quote against it and mark the category `accepted`. Inventing sponsor prose to make the
  // sample score better manufactures evidence and then certifies it.
  sponsorContext: '',
};

/**
 * The core drivers the engine needs to underwrite at all — without these every metric is
 * UNAVAILABLE, so the form requires them before a (model-billing) run.
 */
export const CORE_REQUIRED: (keyof DealFormState)[] = [
  'noi',
  'totalCost',
  'loanAmount',
  'exitCapRatePct',
  'totalUnits',
];

/** Which core-required fields are still blank. Empty array ⇒ the form is runnable. */
export function missingCoreFields(form: DealFormState): (keyof DealFormState)[] {
  return CORE_REQUIRED.filter((k) => (form[k] as string).trim() === '');
}

/** Parse a string to a finite number, or null if blank/invalid. */
function num(value: string): number | null {
  const t = value.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** A percent string ("7", "5.5") → ratio (0.07, 0.055), or null if blank/invalid. */
function pctToRatio(value: string): number | null {
  const n = num(value);
  return n === null ? null : n / 100;
}

function str(value: string): string | null {
  const t = value.trim();
  return t === '' ? null : t;
}

/**
 * Map the form to the api `DealInputs` payload, plus the three fields that ride BESIDE the deal rather
 * than inside it: the reviewer context, the sponsor name, and the sponsor evidence. None is a
 * `DealInputs` field — the engine reads none of them, and folding an entity name or a track record
 * into the proforma's numeric claim is the same category error the `site` column exists to avoid.
 */
export function deriveDeal(form: DealFormState): {
  deal: DealInputs;
  reviewerContext: string | null;
  sponsorName: string | null;
  sponsorContext: string | null;
} {
  const concession = pctToRatio(form.concessionRatePct);
  return {
    deal: {
      noi: num(form.noi),
      annualDebtService: num(form.annualDebtService),
      totalCost: num(form.totalCost),
      loanAmount: num(form.loanAmount),
      totalUnits: num(form.totalUnits),
      exitCapRate: pctToRatio(form.exitCapRatePct),
      vacancyRate: pctToRatio(form.vacancyRatePct),
      expenseRatio: pctToRatio(form.expenseRatioPct),
      salePrice: num(form.salePrice),
      holdYears: num(form.holdYears),
      assetClass: str(form.assetClass),
      propertyClass: str(form.propertyClass),
      isLIHTC: form.isLIHTC,
      marketComps: concession === null ? null : { marketWideConcessionRate: concession },
    },
    reviewerContext: str(form.reviewerContext),
    sponsorName: str(form.sponsorName),
    sponsorContext: str(form.sponsorContext),
  };
}
